import React, { useState, useEffect } from 'react';
import { Form, Button, Table, Empty, Skeleton, Divider, message, Space, Typography, Row, Col, Card, Statistic } from 'antd';
import { HomeOutlined, UnorderedListOutlined, FileSearchOutlined, CloseCircleOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import PageApp from '../../../Page';
import { obtenerHistoricosPorPeriodo } from '../../../../apis/HistoricoNominaApi';
import Currency from '../../../../components/common/Currency';
import { status_period } from '../../../../constants/period';
import { status_recibo_nomina } from '../../../../constants/reciboNomina';
import AnioPeriodo from '../../../../components/forms/anio_periodo';

const { Text } = Typography;

const routes = [
  {
    href: '/',
    title: <>
      <HomeOutlined />
      <span>Inicio</span>
    </>,
  },
  {
    href: '/timbrados-periodo',
    title: (
      <>
        <UnorderedListOutlined />
        <span>Timbrados por Período</span>
      </>
    ),
  }
];

const TimbradosPeriodoPage = () => {
  const [form] = Form.useForm();
  const [historicData, setHistoricData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bitacoraInfo, setBitacoraInfo] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [resumen2, setResumen2] = useState(null);
  const [periodData, setPeriodData] = useState(null);
  const [header, setHeader] = useState(null);

  const getPeriodoText = (statusPeriodo) => {
    if (statusPeriodo === status_period.abierto) return 'Abierto';
    if (statusPeriodo === status_period.cerrado) return 'Cerrado';
    if (statusPeriodo === status_period.timbrado_completo) return 'Timbrado Completo';
    if (statusPeriodo === status_period.timbrado_parcial) return 'Timbrado Parcial';
    return 'Desconocido';
  };

  const onBlurHeader = async (values, dataPeriod, status) => {
    setHeader(values);
    setPeriodData(dataPeriod);
    await obtenerHistorico(values.periodo_id, values.anio, values.num_periodo);
  };

  const getPeriodDetails = async (periodDetails) => {
    // Aquí iría la lógica si es necesaria
  };

  const obtenerHistorico = async (periodoId, anio, numero) => {
    if (!periodoId || !anio || !numero) {
      message.warning('Por favor seleccione un período');
      return;
    }

    setLoading(true);
    try {
      const response = await obtenerHistoricosPorPeriodo(periodoId, anio, numero);
      if (response.ok) {
        let filteredByStatus = (response.payload || []).filter(
          item => item.timbrado === "Timbrado"
        );
        
        // Si no hay registros con "Timbrado", buscar "Si Timbrado de Prueba"
        if (filteredByStatus.length === 0) {
          filteredByStatus = (response.payload || []).filter(
            item => item.timbrado === "Si Timbrado de Prueba"
          );
        }

        const formattedData = filteredByStatus.map(item => ({
          ...item,
          periodo_tipo: getPeriodoText(item.status),
          timbrado: item.timbrado || 'No',
          key: item.id,
        }));

        setHistoricData(formattedData);

        // Calcular bitacoraInfo
        const usuarios_procesados = filteredByStatus.length;
        const usuarios_procesados_exito = filteredByStatus.filter(
          item => item.success === true
        ).length;
        const usuarios_procesados_error = usuarios_procesados - usuarios_procesados_exito;

        setBitacoraInfo({
          usuarios_procesados,
          usuarios_procesados_exito,
          usuarios_procesados_error,
        });

        // Calcular resumen
        const percepcionTotal = filteredByStatus.reduce(
          (sum, item) => sum + (parseFloat(item.percepcion) || 0),
          0
        );
        const deduccionTotal = filteredByStatus.reduce(
          (sum, item) => sum + (parseFloat(item.deduccion) || 0),
          0
        );
        const totalNeto = percepcionTotal - deduccionTotal;

        setResumen({
          percepcionTotal,
          deduccionTotal,
          totalNeto,
          numEmpleados: filteredByStatus.length,
        });

        // Calcular resumen2
        const recibosTimbrados = filteredByStatus.filter(
          item => item.timbrado === 'Timbrado'
        ).length;
        const timbradosPrueba = filteredByStatus.filter(
          item => item.pac === 'CFDI33_Prueba'
        ).length;
        const timbradosError = usuarios_procesados_error;
        const empNeto = filteredByStatus.filter(item => item.neto < 0).length;

        setResumen2({
          empNeto,
          recibosTimbrados,
          timbradosPrueba,
          timbradosError,
        });

        message.success('Histórico cargado correctamente');
      } else {
        message.error('Error al obtener datos históricos');
        setHistoricData([]);
        setBitacoraInfo(null);
        setResumen(null);
        setResumen2(null);
      }
    } catch (error) {
      console.error('Error al obtener histórico:', error);
      message.error('Error al cargar datos históricos');
      setHistoricData([]);
      setBitacoraInfo(null);
      setResumen(null);
      setResumen2(null);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Empleado',
      dataIndex: 'empleado',
      key: 'empleado',
      width: 200,
    },
    {
      title: 'Periodo',
      dataIndex: 'periodo_tipo',
      key: 'periodo_tipo',
      width: 150,
      align: 'center',
    },
    {
      title: 'Año',
      dataIndex: 'anio',
      key: 'anio',
      width: 60,
      align: 'center',
    },
    {
      title: 'Num. Periodo',
      dataIndex: 'numero',
      key: 'numero',
      width: 100,
      align: 'center',
    },
    {
      title: 'Días',
      dataIndex: 'dias',
      key: 'dias',
      width: 60,
      align: 'center',
      render: (value) => Number(value).toFixed(2),
    },
    {
      title: 'Horas Ord',
      dataIndex: 'horas',
      key: 'horas',
      width: 80,
      align: 'center',
      render: (value) => Number(value).toFixed(2),
    },
    {
      title: 'Horas Extra',
      dataIndex: 'horas_extras',
      key: 'horas_extras',
      width: 90,
      align: 'center',
      render: (value) => Number(value).toFixed(2),
    },
    {
      title: 'Percepción',
      dataIndex: 'percepcion',
      key: 'percepcion',
      width: 100,
      align: 'right',
      render: (value) => <Currency value={value} />,
    },
    {
      title: 'Deducción',
      dataIndex: 'deduccion',
      key: 'deduccion',
      width: 100,
      align: 'right',
      render: (value) => <Currency value={value} />,
    },
    {
      title: 'Neto',
      dataIndex: 'neto',
      key: 'neto',
      width: 100,
      align: 'right',
      render: (value) => <Currency value={value} />,
    },
    {
      title: 'Fecha Pago',
      dataIndex: 'fecha_pago',
      key: 'fecha_pago',
      width: 110,
      align: 'center',
    },
    {
      title: 'Timbrado',
      dataIndex: 'timbrado',
      key: 'timbrado',
      width: 150,
      align: 'center',
    },
    {
      title: 'UUID',
      dataIndex: 'uuid',
      key: 'uuid',
      width: 150,
    },
    {
      title: 'PAC',
      dataIndex: 'pac',
      key: 'pac',
      width: 150,
    },
    {
      title: 'Fecha Timbre',
      dataIndex: 'fecha_timbre',
      key: 'fecha_timbre',
      width: 110,
      align: 'center',
    },
  ];

  const filteredColumns = columns.filter(col => {
    if (historicData.length === 0) return true;
    return historicData.some(item => item[col.dataIndex] !== undefined);
  });

  return (
    <PageApp 
      routes={routes}
      title={'Timbrados por Período'}>
      
      <Card>
        <Row gutter={16}>
          <Col xs={24}>
            <AnioPeriodo 
              onBlur={onBlurHeader} 
              getPeriodDetails={getPeriodDetails} 
              form={form}
            />
          </Col>
        </Row>
        
        <Row style={{ marginTop: 16 }}>
          <Col>
            <Space>
              <Button 
                onClick={() => {
                  form.resetFields();
                  setHistoricData([]);
                  setBitacoraInfo(null);
                  setResumen(null);
                  setResumen2(null);
                  setPeriodData(null);
                }}
                icon={<CloseCircleOutlined />}
              >
                Limpiar
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Estadísticas de la bitácora */}
      {bitacoraInfo && (
        <Row gutter={16} style={{ paddingTop: 10, paddingBottom: 10, marginTop: 16 }}>
          <Col span={7} offset={1}>
            <Statistic title="Procesados" value={bitacoraInfo.usuarios_procesados} suffix={`/ ${bitacoraInfo.usuarios_procesados}`} />
          </Col>
          <Col span={7}>
            <Statistic title="Correctos" value={bitacoraInfo.usuarios_procesados_exito} suffix={`/ ${bitacoraInfo.usuarios_procesados}`} prefix={<CheckOutlined style={{ color: 'green' }} />} />
          </Col>
          <Col span={7}>
            <Statistic title="Incorrectos" value={bitacoraInfo.usuarios_procesados_error} suffix={`/ ${bitacoraInfo.usuarios_procesados}`} prefix={<CloseOutlined style={{ color: 'red' }} />} />
          </Col>
        </Row>
      )}

      {/* Sección de resumen */}
      {loading ? (
        <Skeleton active={loading} />
      ) : (<>
          {
            resumen && resumen2 && <Card title="Resumen" className="mb-4" style={{ marginTop: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Row className="mb-2">
                    <Col span={12}><Text>Percepción Total</Text></Col>
                    <Col span={12} style={{ textAlign: 'right' }}><Currency value={resumen.percepcionTotal} /></Col>
                  </Row>
                  <Row className="mb-2">
                    <Col span={12}><Text>Deducción Total</Text></Col>
                    <Col span={12} style={{ textAlign: 'right' }}><Currency value={resumen.deduccionTotal} /></Col>
                  </Row>
                  <Row className="mb-2">
                    <Col span={12}><Text>Total Neto</Text></Col>
                    <Col span={12} style={{ textAlign: 'right' }}><Currency value={resumen.totalNeto} /></Col>
                  </Row>
                  <Row className="mb-2">
                    <Col span={12}><Text>Num. Empleados</Text></Col>
                    <Col span={12} style={{ textAlign: 'right' }}>{resumen.numEmpleados}</Col>
                  </Row>
                </Col>
                <Col span={8}>
                  <Row className="mb-2">
                    <Col span={16}><Text>Emp Neto &lt;</Text></Col>
                    <Col span={8} style={{ textAlign: 'right' }}>{resumen2.empNeto}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col span={16}><Text>Recibos Timbrados</Text></Col>
                    <Col span={8} style={{ textAlign: 'right' }}>{resumen2.recibosTimbrados}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col span={16}><Text>Timbrados Prueba</Text></Col>
                    <Col span={8} style={{ textAlign: 'right' }}>{resumen2.timbradosPrueba}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col span={16}><Text>Timbrados Error</Text></Col>
                    <Col span={8} style={{ textAlign: 'right' }}>{resumen2.timbradosError}</Col>
                  </Row>
                </Col>
              </Row>
            </Card>
          }
        </>
      )}
      <br />
      
      <Card style={{ marginTop: 16 }}>
        {loading ? (
          <Skeleton active={loading} />
        ) : (
          <Table 
            dataSource={historicData} 
            columns={filteredColumns} 
            pagination={{ pageSize: 15 }}
            size="small"
            bordered
            scroll={{ x: 'max-content' }}
            rowKey="id"
            rowClassName={(record, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
            locale={{
              emptyText: <Empty description="No hay datos disponibles para este período" />
            }}
            style={{ 
              marginTop: 16,
              marginBottom: 16
            }}
          />
        )}
      </Card>
      
      <style jsx global>{`
        .table-row-light {
          background-color: #ffffff;
        }
        .table-row-dark {
          background-color: #f5f5f5;
        }
        .ant-table-thead > tr > th {
          background-color: #f0f0f0;
          text-align: center;
          font-weight: bold;
        }
      `}</style>
    </PageApp>
  );
};

export default TimbradosPeriodoPage;
